import { useState } from 'react';
import { Link } from 'react-router-dom';

import { ApiError, api } from '../lib/api';
import { Alert, Field } from '../components/ui';
import { GoogleButton } from '../public/GoogleButton';
import { AuthShell } from '../public/PublicShell';

export function Signup({ onAuthenticated }: { onAuthenticated: () => Promise<void> }) {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState<string | null>(null);
  // Field-specific problems go on the field. `email_taken` and a short
  // password are both about one input, and a banner above the form makes the
  // reader hunt for which one.
  const [emailError, setEmailError] = useState<string | null>(null);
  const [passwordError, setPasswordError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  // The flow still pauses on success, but to say what the account can do rather
  // than to hand over secrets: signup mints no keys, so there is nothing here
  // that exists only once.
  const [created, setCreated] = useState<{ domain: string } | null>(null);

  const MIN_PASSWORD = 12;

  async function submit(event: React.FormEvent) {
    event.preventDefault();
    setError(null);
    setEmailError(null);
    setPasswordError(null);

    // Checked here as well as on the server, not instead of it. The server's
    // rule is the one that counts; this one saves a round trip and puts the
    // answer on the field rather than above the form.
    if (password.length < MIN_PASSWORD) {
      setPasswordError(
        `Use at least ${MIN_PASSWORD} characters — ${password.length} so far.`,
      );
      return;
    }

    setBusy(true);
    try {
      const result = await api.auth.signup(email, password);
      setCreated({ domain: result.tenant.sending_domain });
    } catch (err) {
      // A rejection the server attributes to one field is shown on that field.
      if (err instanceof ApiError && err.code === 'email_taken') {
        setEmailError('An account already exists for this address. Log in instead.');
      } else {
        setError(err instanceof Error ? err.message : 'Sign up failed');
      }
    } finally {
      setBusy(false);
    }
  }

  if (created) {
    return (
      <AuthShell crossLink={{ to: '/login', label: 'Log in' }}>
        <div className="auth-card wide">
          <h1>Account created</h1>

          <Alert kind="ok">
            You can send immediately from <code>{created.domain}</code> — it inherits
            verification from the platform domain, so there is no DNS to set up first.
          </Alert>

          <Alert kind="info">
            Create an API key from <strong>API keys</strong> to start sending. The key
            is shown once, when you create it, so you can copy it then — make a live
            key for real mail and a test key for everything else. The test key runs
            the whole pipeline — suppression, events, webhooks — without sending real
            mail or spending quota.
          </Alert>

          <button
            className="btn-primary btn-block"
            onClick={() => void onAuthenticated()}
          >
            Continue to dashboard
          </button>
        </div>
      </AuthShell>
    );
  }

  return (
    <AuthShell crossLink={{ to: '/login', label: 'Already have an account? Log in' }}>
      <h1>Create your account</h1>
      <p className="auth-sub">Start sending in a few minutes. No credit card required.</p>

      <GoogleButton label="Sign up with Google" />

      <form onSubmit={submit}>

        {error && <Alert kind="error">{error}</Alert>}

        <Field id="email" label="Work email" error={emailError}>
          {(props) => (
            <input
              {...props}
              type="email"
              autoComplete="username"
              placeholder="you@company.com"
              required
              value={email}
              onChange={(e) => {
                setEmail(e.target.value);
                // A corrected field must stop showing a stale error.
                if (emailError) setEmailError(null);
              }}
            />
          )}
        </Field>

        <Field
          id="password"
          label="Password"
          error={passwordError}
          hint="At least 12 characters. Length beats punctuation."
        >
          {(props) => (
            <input
              {...props}
              type="password"
              autoComplete="new-password"
              // The mockup says 8; the server's MIN_PASSWORD_LENGTH is 12, and
              // promising 8 would get the form rejected.
              placeholder="At least 12 characters"
              minLength={MIN_PASSWORD}
              required
              value={password}
              onChange={(e) => {
                setPassword(e.target.value);
                if (passwordError) setPasswordError(null);
              }}
            />
          )}
        </Field>

        <button className="btn-primary btn-block" disabled={busy}>
          {busy ? 'Creating…' : 'Create account'}
        </button>
      </form>

      <p className="switch">
        Already have an account? <Link to="/login">Log in</Link>
      </p>
    </AuthShell>
  );
}
